/**
 * DVL Phase 4 — Financial Truth Dashboard
 *
 * Single page showing the unified financial reality:
 *  • Top ribbon (page-level health)
 *  • 4 entity cards (value + source badge)
 *  • Smart explanations for any non-green entity
 *  • Confidence score gauge
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { FinancialHealthRibbon } from '../components/FinancialHealthRibbon';
import {
  Loader2, ScrollText, RefreshCw, Sparkles, AlertTriangle,
  TrendingUp, Users, Briefcase, Building2,
} from 'lucide-react';

const ENTITY_META = {
  trial_balance:      { icon: ScrollText,  label_ar: 'ميزان المراجعة',     accent: 'violet' },
  project_profit:     { icon: TrendingUp,  label_ar: 'ربحية المشاريع',     accent: 'emerald' },
  customer_balances:  { icon: Users,       label_ar: 'أرصدة العملاء',      accent: 'sky' },
  supplier_balances:  { icon: Building2,   label_ar: 'أرصدة الموردين',     accent: 'amber' },
};

const BADGE_COLOR = {
  green:  { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  yellow: { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200' },
  blue:   { bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200' },
  grey:   { bg: 'bg-gray-50',    text: 'text-gray-600',    border: 'border-gray-200' },
};

const FinancialTruthDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await axiosInstance.get('/financial/dashboard');
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'فشل تحميل لوحة الحقيقة المالية');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axiosInstance.get('/financial/dashboard');
        if (!cancelled) { setData(r.data); setErr(null); }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || 'فشل تحميل لوحة الحقيقة المالية');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center text-gray-500">
      <Loader2 className="w-6 h-6 animate-spin mr-2" /> جاري تحميل لوحة الحقيقة المالية…
    </div>
  );
  if (err) return (
    <div className="p-6 text-rose-700 bg-rose-50 border border-rose-200 rounded-lg m-6">
      {err}
    </div>
  );

  const ents = data.entities || {};
  const exps = data.explanations || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="financial-truth-dashboard">
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Title */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs text-violet-600 font-bold uppercase tracking-wider flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4" /> DVL · Financial Truth Layer
            </div>
            <h1 className="text-2xl font-bold text-gray-900">لوحة الحقيقة المالية</h1>
            <p className="text-sm text-gray-500 mt-1">
              كل رقم يحمل مصدره ومستوى ثقته — لا غموض، لا اختفاء بيانات.
            </p>
          </div>
          <button
            data-testid="ftd-refresh"
            onClick={load}
            disabled={refreshing}
            className="px-4 py-2.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 flex items-center gap-2 font-semibold text-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> تحديث
          </button>
        </div>

        {/* Ribbon */}
        <FinancialHealthRibbon />

        {/* Entity cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ftd-entities">
          {Object.entries(ents).map(([name, e]) => {
            const meta = ENTITY_META[name] || { icon: ScrollText, label_ar: name, accent: 'gray' };
            const Icon = meta.icon;
            const badge = e.badge || {};
            const palette = BADGE_COLOR[badge.code] || BADGE_COLOR.grey;
            return (
              <div
                key={name}
                data-testid={`ftd-entity-${name}`}
                className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-3"
              >
                <div className="flex items-center justify-between">
                  <div className={`w-9 h-9 rounded-lg bg-${meta.accent}-100 text-${meta.accent}-700 flex items-center justify-center`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${palette.bg} ${palette.text} ${palette.border}`}
                    title={badge.tooltip}
                    data-testid={`ftd-badge-${name}`}
                  >
                    {badge.symbol} {badge.label_ar || badge.label}
                  </span>
                </div>
                <div>
                  <div className="text-xs text-gray-500 font-semibold">{meta.label_ar}</div>
                  <div className="text-2xl font-mono font-bold text-gray-900 mt-1" data-testid={`ftd-value-${name}`}>
                    {e.value?.display ?? '—'}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-1">
                    {e.value?.row_count ?? 0} صف · {e.meta?.duration_ms?.toFixed(0) ?? '?'} ms
                  </div>
                </div>
                {e.meta?.fallback_reason && (
                  <div className="text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {e.meta.fallback_reason}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Explanations */}
        {Object.keys(exps).length > 0 && (
          <div className="bg-white rounded-xl border border-amber-200 p-5" data-testid="ftd-explanations">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <h2 className="font-bold text-gray-900">تفسير الفجوات المُكتشفة</h2>
            </div>
            <div className="space-y-4">
              {Object.entries(exps).map(([name, exp]) => (
                <div key={name} className="border-l-4 border-amber-400 pl-4" data-testid={`ftd-explain-${name}`}>
                  <div className="font-semibold text-gray-800 text-sm">
                    {ENTITY_META[name]?.label_ar || name}
                  </div>
                  <div className="text-sm text-gray-700 mt-1">{exp.explanation_ar}</div>
                  <ul className="mt-2 space-y-1">
                    {(exp.items || []).map((it, i) => (
                      <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                        <span>
                          <b className="text-gray-800">{it.label_ar}</b>
                          {' '}<span className="font-mono text-amber-700">({it.count})</span>
                          {it.action_en && (
                            <div className="text-[10px] text-gray-500 mt-0.5 font-mono">
                              → {it.action_en}
                            </div>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialTruthDashboard;
